import React from 'react';
import { styled } from '@mui/material/styles';

import { hexToRgb } from '../../../services/styleUtils/colorUtils.js';

type KitCardColor = 'primary' | 'info' | 'success' | 'warning' | 'error';

interface KitCardProps {
  className?: string;
  children?: React.ReactNode;
  plain?: boolean;
  profile?: boolean;
  blog?: boolean;
  raised?: boolean;
  background?: boolean;
  pricing?: boolean;
  color?: KitCardColor;
  product?: boolean;
  [key: string]: unknown;
}

interface KitCardStyleProps {
  plain?: boolean;
  profile?: boolean;
  blog?: boolean;
  raised?: boolean;
  background?: boolean;
  pricing?: boolean;
  product?: boolean;
  color?: KitCardColor;
}

export default function KitCard({
  className,
  children,
  plain = false,
  profile = false,
  blog = false,
  raised = false,
  background = false,
  pricing = false,
  color,
  product = false,
  ...rest
}: KitCardProps) {
  return (
    <Container
      className={className}
      {...rest}
      plain={plain}
      profile={profile}
      blog={blog}
      raised={raised}
      background={background}
      pricing={pricing}
      product={product}
      color={color}
    >
      {children}
    </Container>
  );
}

const Container = styled('div', {
  shouldForwardProp: (prop) =>
    ![
      'plain',
      'profile',
      'blog',
      'raised',
      'background',
      'pricing',
      'product',
      'color',
    ].includes(prop as string),
})<KitCardStyleProps>(({ theme, color = 'primary' }) => ({
  border: '0',
  marginBottom: '30px',
  marginTop: '30px',
  borderRadius: '6px',
  color: 'rgba(' + hexToRgb(theme.palette.common.black) + ', 0.87)',
  background: theme.palette.common.white,
  width: '100%',
  boxShadow:
    '0 2px 2px 0 rgba(' +
    hexToRgb(theme.palette.common.black) +
    ', 0.14), 0 3px 1px -2px rgba(' +
    hexToRgb(theme.palette.common.black) +
    ', 0.2), 0 1px 5px 0 rgba(' +
    hexToRgb(theme.palette.common.black) +
    ', 0.12)',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minWidth: '0',
  wordWrap: 'break-word',
  fontSize: '.875rem',
  // some jss/css to make the cards look a bit better on Internet Explorer
  '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
    display: 'inline-block !important',
  },
  variants: [
    {
      props: ({ plain }) => plain,
      style: {
        background: 'transparent',
        boxShadow: 'none',
      },
    },
    {
      props: ({ profile }) => profile,
      style: {
        marginTop: '30px',
        textAlign: 'center',
      },
    },
    {
      props: ({ blog }) => blog,
      style: {
        marginTop: '60px',
      },
    },
    {
      props: ({ raised }) => raised,
      style: {
        boxShadow:
          '0 16px 38px -12px rgba(' +
          hexToRgb(theme.palette.common.black) +
          ', 0.56), 0 4px 25px 0px rgba(' +
          hexToRgb(theme.palette.common.black) +
          ', 0.12), 0 8px 10px -5px rgba(' +
          hexToRgb(theme.palette.common.black) +
          ', 0.2)',
      },
    },
    {
      props: ({ background }) => background,
      style: {
        backgroundPosition: '50%',
        backgroundSize: 'cover',
        textAlign: 'center',
        color: theme.palette.common.white,
        '& h3': {
          color: theme.palette.common.white + ' !important',
        },
        '& p': {
          color:
            'rgba(' + hexToRgb(theme.palette.common.white) + ',0.7)!important',
        },
        '&:after': {
          position: 'absolute',
          zIndex: '1',
          width: '100%',
          height: '100%',
          display: 'block',
          left: '0',
          top: '0',
          content: '""',
          backgroundColor:
            'rgba(' + hexToRgb(theme.palette.common.black) + ', 0.56)',
          borderRadius: '6px',
        },
        '& small': {
          color:
            'rgba(' +
            hexToRgb(theme.palette.common.white) +
            ', 0.7) !important',
        },
      },
    },
    {
      props: ({ pricing }) => pricing,
      style: {
        textAlign: 'center',
        '&:after': {
          backgroundColor:
            'rgba(' +
            hexToRgb(theme.palette.common.black) +
            ', 0.7) !important',
        },
        '& ul': {
          listStyle: 'none',
          padding: 0,
          maxWidth: '240px',
          margin: '10px auto',
        },
        '& ul li': {
          color: theme.palette.grey[500],
          textAlign: 'center',
          padding: '12px 0px',
          borderBottom:
            '1px solid rgba(' + hexToRgb(theme.palette.grey[500]) + ',0.3)',
        },
        '& ul li:last-child': {
          border: 0,
        },
        '& ul li b': {
          color: theme.palette.grey[400],
        },
        '& h1': {
          marginTop: '30px',
        },
        '& h1 small': {
          display: 'inline-flex',
          height: 0,
          fontSize: '18px',
        },
        '& h1 small:first-child': {
          position: 'relative',
          top: '-17px',
          fontSize: '26px',
        },
        '& ul li svg,& ul li .fab,& ul li .fas,& ul li .far,& ul li .fal,& ul li .material-icons':
          {
            position: 'relative',
            top: '7px',
          },
      },
    },
    {
      props: ({ color, pricing }) => pricing && color,
      style: {
        '& ul li': {
          color: theme.palette.common.white,
          borderColor: 'rgba(' + hexToRgb(theme.palette.common.white) + ',0.3)',
          '& b, & svg,& .fab,& .fas,& .far,& .fal,& .material-icons': {
            color: theme.palette.common.white,
            fontWeight: '700',
          },
        },
      },
    },
    {
      props: ({ product }) => product,
      style: {
        marginTop: '30px',
      },
    },
    {
      props: ({ color }) => color,
      style: {
        background:
          'linear-gradient(60deg,' +
          theme.palette[color].light +
          ', ' +
          theme.palette[color].dark +
          ')',
        '& h1 small': {
          color: 'rgba(' + hexToRgb(theme.palette.common.white) + ', 0.8)',
        },
        color: theme.palette.common.white,
      },
    },
  ],
}));
